import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Download, Loader2, AlertCircle, FileText, CheckCircle } from 'lucide-react';
import { GLOBAL_BACKEND_URL } from '../config';

const BASE_URL = GLOBAL_BACKEND_URL;

export default function DocumentViewer() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const owner = searchParams.get('owner');  // admin panel: document of another user
  const back = searchParams.get('back');
  const [documentContent, setDocumentContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedPage, setCopiedPage] = useState(null);

  useEffect(() => {
    if (user && documentId) loadDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, documentId, owner]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/documents/${documentId}/?uuid=${encodeURIComponent(owner || user.uid)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setDocumentContent(await response.json());
      else setDocumentContent(null);
    } catch (error) {
      console.error('Error loading document:', error);
      setDocumentContent(null);
    } finally {
      setLoading(false);
    }
  };

  const copyPageText = (text, pageNum) => {
    navigator.clipboard.writeText(text);
    setCopiedPage(pageNum);
    setTimeout(() => setCopiedPage(null), 2000);
  };

  const copyAllText = () => {
    navigator.clipboard.writeText(documentContent?.extracted_text || '');
    setCopiedPage('all');
    setTimeout(() => setCopiedPage(null), 2000);
  };

  // Merge images and page texts into a unified array
  const buildPages = () => {
    if (!documentContent) return [];
    const images = documentContent.processed_images || [];
    const texts = documentContent.pages || [];

    const maxLen = Math.max(images.length, texts.length, 1);
    const pages = [];
    for (let i = 0; i < maxLen; i++) {
      pages.push({
        pageNumber: i + 1,
        imageUrl: images[i]?.url || null,
        text: texts[i]?.text || '',
      });
    }
    return pages;
  };

  const pages = documentContent ? buildPages() : [];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(back && back.startsWith('/admin') ? back : documentContent?.metadata?.folder_unique_id ? `/dashboard?folder=${documentContent.metadata.folder_unique_id}` : '/dashboard')}
            className="p-2.5 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">
              {documentContent?.metadata?.original_filename || documentId}
            </h2>
            {pages.length > 0 && (
              <p className="text-sm text-gray-400 mt-0.5">
                {pages.length} page{pages.length !== 1 ? 's' : ''}
                {(documentContent?.metadata?.model_used || documentContent?.metadata?.model) &&
                  ` · transcribed with ${documentContent.metadata.model_used || documentContent.metadata.model}`}
              </p>
            )}
          </div>
          <button
            onClick={copyAllText}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-600"
          >
            {copiedPage === 'all' ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : 'Copy All Text'}
          </button>
          {documentContent?.original_url && (
            <a
              href={documentContent.original_url}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-400">Loading document...</p>
          </div>
        ) : !documentContent ? (
          <div className="text-center py-20">
            <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load</h3>
            <p className="text-sm text-gray-500">Unable to retrieve document content</p>
          </div>
        ) : (
          <div className="space-y-8">
            {pages.map((page) => (
              <div
                key={page.pageNumber}
                className="border border-gray-200 rounded-2xl overflow-hidden"
              >
                {/* Page header */}
                <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-500">
                    Page {page.pageNumber}
                  </span>
                  <button
                    onClick={() => copyPageText(page.text, page.pageNumber)}
                    className="px-3 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {copiedPage === page.pageNumber ? (
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      'Copy'
                    )}
                  </button>
                </div>

                {/* Two columns: scan + text */}
                <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                  {/* Left: scanned image */}
                  <div className="p-4 bg-gray-50/50 flex items-start justify-center min-h-[300px]">
                    {page.imageUrl ? (
                      <img
                        src={page.imageUrl}
                        alt={`Page ${page.pageNumber}`}
                        className="max-w-full h-auto rounded-lg shadow-sm"
                        style={{ maxHeight: '700px' }}
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-300">
                        <FileText className="h-10 w-10 mb-2" />
                        <span className="text-sm">No scan available</span>
                      </div>
                    )}
                  </div>

                  {/* Right: extracted text */}
                  <div className="p-5 max-h-[700px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 leading-relaxed">
                      {page.text || 'No text extracted for this page'}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}